import React, { useState } from 'react';
import { ArrowRight, Menu, X, ChevronDown, Shield, Zap, Heart, Target } from 'lucide-react';
import 'flag-icons/css/flag-icons.min.css';

type Language = 'en' | 'fr' | 'ar';

const translations = {
    en: {
        nav: { features: 'Features', benefits: 'Benefits', pricing: 'Pricing', login: 'Log in', getStarted: 'Get Started', about: 'About', home: 'Home' },
        hero: {
            tagline: 'About Karia.tn',
            title1: 'Empowering', title2: 'Car Rental', title3: 'Agencies',
            description: 'We believe every car rental business deserves access to powerful, intuitive technology that drives growth and simplifies operations.',
        },
        founders: {
            label: 'Meet Our Team',
            title: 'Built by Industry Experts',
            description: 'Our leadership combines deep automotive industry knowledge with cutting-edge technology expertise.',
            founder: {
                name: 'Hachem Sekri',
                role: 'Founder & CEO',
                bio: 'With over 15 years in the automotive and rental industry, Hachem identified the need for a comprehensive ERP solution tailored specifically for car rental agencies. His vision drives Karia\'s mission to transform how rental businesses operate.',
                expertise: ['Industry Leadership', 'Strategic Vision', 'Business Development', 'Market Analysis'],
            },
            cofounder: {
                name: 'Mooaz Sayyed',
                role: 'Cloud Consultant',
                bio: 'Mooaz brings exceptional technical expertise and innovation to Karia. As our lead developer, he transforms complex business requirements into elegant, user-friendly solutions that rental agencies love to use.',
                expertise: ['DevOps', 'System Architecture', 'UI/UX Design', 'Cloud Solutions'],
            },
        },
        mission: {
            label: 'Our Mission',
            title: 'Transforming Car Rental Operations',
            description: 'We\'re on a mission to revolutionize how car rental agencies manage their business, from small local operators to large enterprise fleets.',
            values: [
                {
                    title: 'Innovation First',
                    desc: 'We continuously evolve our platform with the latest technology trends and customer feedback.',
                    icon: 'Zap'
                },
                {
                    title: 'Customer Success',
                    desc: 'Your success is our success. We\'re committed to helping every agency grow and thrive.',
                    icon: 'Heart'
                },
                {
                    title: 'Reliability',
                    desc: 'Rock-solid infrastructure and 99.9% uptime ensure your business never stops.',
                    icon: 'Shield'
                },
                {
                    title: 'Simplicity',
                    desc: 'Complex operations made simple through intuitive design and smart automation.',
                    icon: 'Target'
                },
            ],
        },
        story: {
            label: 'Our Story',
            title: 'From Problem to Solution',
            description: 'Born from real industry challenges, Karia was created to solve the fragmented software landscape that car rental agencies face daily.',
            timeline: [
                {
                    year: '2023',
                    title: 'The Spark',
                    desc: 'Hachem identified the gap in comprehensive rental management solutions while working with multiple agencies.',
                },
                {
                    year: '2024',
                    title: 'Development Begins',
                    desc: 'Mooaz joined as co-partner, and together they began building the first version of Karia with direct input from rental agencies.',
                },
                {
                    year: '2025',
                    title: 'First Launch',
                    desc: 'Successfully launched with pilot agencies in Tunisia, receiving overwhelmingly positive feedback and rapid adoption.',
                },
                {
                    year: '2026',
                    title: 'Global Expansion',
                    desc: 'Today, Karia serves rental agencies worldwide, continuing to innovate and expand our feature set.',
                },
            ],
        },
        cta: {
            title: 'Ready to Transform Your Agency?',
            description: 'Join hundreds of rental agencies that have already revolutionized their operations with Karia.',
            primary: 'See Live Demo',
            secondary: 'Schedule a Demo',
        },
        footer: {
            tagline: 'Complete ERP solution for modern car rental agencies.',
            product: 'Product', company: 'Company', legal: 'Legal',
            features: 'Features', pricingLink: 'Pricing', updates: 'Updates',
            about: 'About', blog: 'Blog', contact: 'Contact',
            privacy: 'Privacy', terms: 'Terms', security: 'Security',
            rights: '© 2026 karia.tn. All rights reserved.'
        },
    },
    fr: {
        nav: { features: 'Fonctionnalités', benefits: 'Avantages', pricing: 'Tarifs', login: 'Connexion', getStarted: 'Commencer', about: 'À propos', home: 'Accueil' },
        hero: {
            tagline: 'À propos de Karia.tn',
            title1: 'Autonomiser', title2: 'les Agences', title3: 'de Location',
            description: 'Nous croyons que chaque entreprise de location de voitures mérite un accès à une technologie puissante et intuitive qui favorise la croissance et simplifie les opérations.',
        },
        founders: {
            label: 'Rencontrez Notre Équipe',
            title: 'Conçu par des Experts du Secteur',
            description: 'Notre direction combine une connaissance approfondie de l\'industrie automobile avec une expertise technologique de pointe.',
            founder: {
                name: 'Hachem Sekri',
                role: 'Fondateur & PDG',
                bio: 'Avec plus de 15 ans dans l\'industrie automobile et de location, Hachem a identifié le besoin d\'une solution ERP complète adaptée spécifiquement aux agences de location de voitures. Sa vision guide la mission de Karia de transformer le fonctionnement des entreprises de location.',
                expertise: ['Leadership Industriel', 'Vision Stratégique', 'Développement Commercial', 'Analyse de Marché'],
            },
            cofounder: {
                name: 'Mooaz Sayyed',
                role: 'Co-Partenaire & Développeur Principal',
                bio: 'Mooaz apporte une expertise technique exceptionnelle et de l\'innovation à Karia. En tant que développeur principal, il transforme des exigences commerciales complexes en solutions élégantes et conviviales que les agences de location adorent utiliser.',
                expertise: ['Développement Full-Stack', 'Architecture Système', 'Design UI/UX', 'Optimisation Base de Données'],
            },
        },
        mission: {
            label: 'Notre Mission',
            title: 'Transformer les Opérations de Location',
            description: 'Nous sommes en mission pour révolutionner la façon dont les agences de location gèrent leur entreprise, des petits opérateurs locaux aux grandes flottes d\'entreprise.',
            values: [
                {
                    title: 'Innovation d\'Abord',
                    desc: 'Nous faisons continuellement évoluer notre plateforme avec les dernières tendances technologiques et les commentaires des clients.',
                    icon: 'Zap'
                },
                {
                    title: 'Succès Client',
                    desc: 'Votre succès est notre succès. Nous nous engageons à aider chaque agence à croître et prospérer.',
                    icon: 'Heart'
                },
                {
                    title: 'Fiabilité',
                    desc: 'Infrastructure solide et disponibilité de 99,9% assurent que votre entreprise ne s\'arrête jamais.',
                    icon: 'Shield'
                },
                {
                    title: 'Simplicité',
                    desc: 'Opérations complexes rendues simples grâce à un design intuitif et une automatisation intelligente.',
                    icon: 'Target'
                },
            ],
        },
        story: {
            label: 'Notre Histoire',
            title: 'Du Problème à la Solution',
            description: 'Né des défis réels de l\'industrie, Karia a été créé pour résoudre le paysage logiciel fragmenté auquel font face quotidiennement les agences de location de voitures.',
            timeline: [
                {
                    year: '2023',
                    title: 'L\'Étincelle',
                    desc: 'Hachem a identifié le gap dans les solutions complètes de gestion de location en travaillant avec plusieurs agences.',
                },
                {
                    year: '2024',
                    title: 'Début du Développement',
                    desc: 'Mooaz a rejoint comme co-partenaire, et ensemble ils ont commencé à construire la première version de Karia avec l\'input direct des agences de location.',
                },
                {
                    year: '2025',
                    title: 'Premier Lancement',
                    desc: 'Lancé avec succès avec des agences pilotes en Tunisie, recevant des retours extrêmement positifs et une adoption rapide.',
                },
                {
                    year: '2026',
                    title: 'Expansion Mondiale',
                    desc: 'Aujourd\'hui, Karia sert des agences de location dans le monde entier, continuant d\'innover et d\'étendre notre gamme de fonctionnalités.',
                },
            ],
        },
        cta: {
            title: 'Prêt à Transformer Votre Agence?',
            description: 'Rejoignez des centaines d\'agences de location qui ont déjà révolutionné leurs opérations avec Karia.',
            primary: 'Voir la Démo',
            secondary: 'Planifier une Démo',
        },
        footer: {
            tagline: 'Solution ERP complète pour les agences de location modernes.',
            product: 'Produit', company: 'Entreprise', legal: 'Légal',
            features: 'Fonctionnalités', pricingLink: 'Tarifs', updates: 'Mises à jour',
            about: 'À Propos', blog: 'Blog', contact: 'Contact',
            privacy: 'Confidentialité', terms: 'Conditions', security: 'Sécurité',
            rights: '© 2026 karia.tn. Tous droits réservés.'
        },
    },
    ar: {
        nav: { features: 'المميزات', benefits: 'الفوائد', pricing: 'الأسعار', login: 'تسجيل الدخول', getStarted: 'ابدأ الآن', about: 'حولنا', home: 'الرئيسية' },
        hero: {
            tagline: 'حول Karia.tn',
            title1: 'تمكين', title2: 'وكالات', title3: 'تأجير السيارات',
            description: 'نؤمن أن كل شركة تأجير سيارات تستحق الوصول إلى تكنولوجيا قوية وبديهية تدفع النمو وتبسط العمليات.',
        },
        founders: {
            label: 'تعرف على فريقنا',
            title: 'بناه خبراء الصناعة',
            description: 'قيادتنا تجمع بين المعرفة العميقة لصناعة السيارات والخبرة التقنية المتطورة.',
            founder: {
                name: 'هاشم السكري',
                role: 'المؤسس والرئيس التنفيذي',
                bio: 'مع أكثر من 15 عاماً في صناعة السيارات والتأجير، حدد هاشم الحاجة لحل ERP شامل مصمم خصيصاً لوكالات تأجير السيارات. رؤيته توجه مهمة كاريا لتحويل كيفية عمل شركات التأجير.',
                expertise: ['القيادة الصناعية', 'الرؤية الاستراتيجية', 'تطوير الأعمال', 'تحليل السوق'],
            },
            cofounder: {
                name: 'معاذ سيد',
                role: 'الشريك المؤسس والمطور الرئيسي',
                bio: 'معاذ يجلب خبرة تقنية استثنائية وابتكار لكاريا. كمطورنا الرئيسي، يحول متطلبات الأعمال المعقدة إلى حلول أنيقة وسهلة الاستخدام تحبها وكالات التأجير.',
                expertise: ['تطوير متكامل', 'هندسة الأنظمة', 'تصميم واجهة المستخدم', 'تحسين قواعد البيانات'],
            },
        },
        mission: {
            label: 'مهمتنا',
            title: 'تحويل عمليات تأجير السيارات',
            description: 'نحن في مهمة لإحداث ثورة في كيفية إدارة وكالات تأجير السيارات لأعمالها، من المشغلين المحليين الصغار إلى الأساطيل الكبيرة للمؤسسات.',
            values: [
                {
                    title: 'الابتكار أولاً',
                    desc: 'نطور منصتنا باستمرار مع أحدث التوجهات التقنية وملاحظات العملاء.',
                    icon: 'Zap'
                },
                {
                    title: 'نجاح العميل',
                    desc: 'نجاحك هو نجاحنا. ملتزمون بمساعدة كل وكالة على النمو والازدهار.',
                    icon: 'Heart'
                },
                {
                    title: 'الموثوقية',
                    desc: 'بنية تحتية قوية وتوفر 99.9% يضمن أن أعمالك لا تتوقف أبداً.',
                    icon: 'Shield'
                },
                {
                    title: 'البساطة',
                    desc: 'عمليات معقدة تُبسط من خلال تصميم بديهي وأتمتة ذكية.',
                    icon: 'Target'
                },
            ],
        },
        story: {
            label: 'قصتنا',
            title: 'من المشكلة إلى الحل',
            description: 'وُلد من تحديات الصناعة الحقيقية، أُنشئ كاريا لحل المشهد البرمجي المجزأ الذي تواجهه وكالات تأجير السيارات يومياً.',
            timeline: [
                {
                    year: '2023',
                    title: 'الشرارة',
                    desc: 'حدد هاشم الفجوة في حلول إدارة التأجير الشاملة أثناء عمله مع وكالات متعددة.',
                },
                {
                    year: '2024',
                    title: 'بداية التطوير',
                    desc: 'انضم معاذ كشريك مؤسس، ومعاً بدؤوا ببناء النسخة الأولى من كاريا بمدخلات مباشرة من وكالات التأجير.',
                },
                {
                    year: '2025',
                    title: 'الإطلاق الأول',
                    desc: 'أُطلق بنجاح مع وكالات تجريبية في تونس، حاصلاً على ردود فعل إيجابية جداً واعتماد سريع.',
                },
                {
                    year: '2026',
                    title: 'التوسع العالمي',
                    desc: 'اليوم، كاريا يخدم وكالات التأجير حول العالم، مستمراً في الابتكار وتوسيع مجموعة ميزاتنا.',
                },
            ],
        },
        cta: {
            title: 'مستعد لتحويل وكالتك؟',
            description: 'انضم لمئات وكالات التأجير التي ثورت عملياتها بالفعل مع كاريا.',
            primary: 'شاهد العرض التوضيحي',
            secondary: 'احجز عرضاً توضيحياً',
        },
        footer: {
            tagline: 'حل ERP متكامل لوكالات تأجير السيارات الحديثة.',
            product: 'المنتج', company: 'الشركة', legal: 'قانوني',
            features: 'المميزات', pricingLink: 'الأسعار', updates: 'التحديثات',
            about: 'حولنا', blog: 'المدونة', contact: 'اتصل بنا',
            privacy: 'الخصوصية', terms: 'الشروط', security: 'الأمان',
            rights: '© 2026 karia.tn. جميع الحقوق محفوظة.'
        },
    },
};

const valueIcons = {
    Zap: Zap,
    Heart: Heart,
    Shield: Shield,
    Target: Target,
};

const AboutPage = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [lang, setLang] = useState<Language>('en');
    const [langMenuOpen, setLangMenuOpen] = useState(false);

    const t = translations[lang];
    const isRTL = lang === 'ar';

    const langLabels: Record<Language, { flagClass: string; name: string; short: string }> = {
        en: { flagClass: 'fi fi-us', name: 'English', short: 'EN' },
        fr: { flagClass: 'fi fi-fr', name: 'Français', short: 'FR' },
        ar: { flagClass: 'fi fi-tn', name: 'العربية', short: 'عربي' }
    };

    return (
        <div className="min-h-screen bg-white text-slate-900" dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Navigation */}
            <nav className="sticky top-0 bg-white/95 backdrop-blur-lg z-50">
                <div className="w-full px-4 md:px-8 py-4 flex items-center justify-between">
                    <a href="/" className="text-4xl md:text-5xl" style={{ fontFamily: 'Caveat, cursive', color: '#273B7D', fontWeight: 700, letterSpacing: '-0.02em' }}>Karia</a>
                    <div className="hidden md:flex items-center gap-12" style={{ fontFamily: 'Inter, sans-serif' }}>
                        <a href="/" className="text-base font-medium text-slate-600 hover:text-slate-900 transition-colors">{t.nav.home}</a>
                        <a href="#mission" className="text-base font-medium text-slate-600 hover:text-slate-900 transition-colors">{t.nav.features}</a>
                        <a href="#story" className="text-base font-medium text-slate-600 hover:text-slate-900 transition-colors">{t.nav.benefits}</a>
                        <div className="flex items-center gap-4 ml-6">
                            <a href="https://app.karia.tn/login" className="text-base font-medium px-5 py-2.5 rounded-lg transition-colors hover:bg-slate-100" style={{ color: '#273B7D' }}>{t.nav.login}</a>
                            <a href="https://demo.karia.tn" className="text-base font-semibold text-white px-7 py-3 rounded-lg transition-all hover:opacity-90" style={{ backgroundColor: '#FF690F' }}>
                                See Live Demo
                            </a>
                            {/* Language Switcher */}
                            <div className="relative">
                                <button onClick={() => setLangMenuOpen(!langMenuOpen)} className="flex items-center gap-2 text-base font-medium text-slate-600 hover:text-slate-900 transition-colors px-4 py-2.5 rounded-full hover:bg-slate-100 border border-slate-200">
                                    <span className={langLabels[lang].flagClass} style={{ fontSize: '1.25rem' }}></span>
                                    <span>{langLabels[lang].name}</span>
                                    <ChevronDown className={`w-4 h-4 transition-transform ${langMenuOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {langMenuOpen && (
                                    <div className="absolute top-full mt-2 bg-white rounded-xl shadow-xl border border-slate-200 py-3 min-w-[160px]" style={{ right: isRTL ? 'auto' : 0, left: isRTL ? 0 : 'auto' }}>
                                        {(['en', 'fr', 'ar'] as Language[]).map((l) => (
                                            <button key={l} onClick={() => { setLang(l); setLangMenuOpen(false); }} className={`w-full px-4 py-3 text-base text-left hover:bg-slate-50 transition-colors flex items-center gap-3 ${lang === l ? 'font-semibold bg-slate-50' : ''}`} style={{ textAlign: isRTL ? 'right' : 'left' }}>
                                                <span className={langLabels[l].flagClass} style={{ fontSize: '1.25rem' }}></span>
                                                <span>{langLabels[l].name}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <button className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                        {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden border-t border-slate-100 bg-white shadow-lg">
                        <div className="px-4 py-6 space-y-4">
                            <a href="/" className="block text-slate-600 hover:text-slate-900 py-2 font-medium">{t.nav.home}</a>
                            <a href="#mission" className="block text-slate-600 hover:text-slate-900 py-2 font-medium">{t.nav.features}</a>
                            <a href="#story" className="block text-slate-600 hover:text-slate-900 py-2 font-medium">{t.nav.benefits}</a>
                            {/* Mobile Language Switcher */}
                            <div className="flex gap-2 py-2">
                                {(['en', 'fr', 'ar'] as Language[]).map((l) => (
                                    <button key={l} onClick={() => setLang(l)} className={`px-4 py-2.5 text-sm rounded-lg border transition-colors flex items-center gap-2 ${lang === l ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 text-slate-600'}`}>
                                        <span className={langLabels[l].flagClass} style={{ fontSize: '1rem' }}></span>
                                        <span>{langLabels[l].short}</span>
                                    </button>
                                ))}
                            </div>
                            <div className="pt-4 border-t border-slate-100 space-y-3">
                                <a href="https://app.karia.tn/login" className="block text-center py-2.5 font-medium rounded-lg border border-slate-200" style={{ color: '#273B7D' }}>{t.nav.login}</a>
                                <a href="https://demo.karia.tn" className="block w-full text-center text-white font-semibold px-5 py-2.5 rounded-lg transition-all" style={{ backgroundColor: '#FF690F' }}>
                                    See Live Demo
                                </a>
                            </div>
                        </div>
                    </div>
                )}
            </nav>

            {/* Hero Section */}
            <section className="relative w-full bg-white pt-16 pb-24 md:pt-20 md:pb-32 overflow-hidden">
                <div className="max-w-6xl mx-auto px-6 text-center">
                    <p className="text-sm md:text-base uppercase tracking-widest text-slate-500 mb-8 font-semibold">
                        {t.hero.tagline}
                    </p>
                    <h1 className="text-5xl md:text-7xl lg:text-8xl font-light mb-8 leading-[0.9] tracking-tight" style={{ color: '#273B7D' }}>
                        {t.hero.title1}
                        <br />
                        <span className="font-semibold" style={{ color: '#FF690F' }}>{t.hero.title2}</span>
                        <br />
                        {t.hero.title3}
                    </h1>
                    <p className="text-xl md:text-2xl text-slate-600 mb-16 max-w-4xl mx-auto leading-relaxed font-light">
                        {t.hero.description}
                    </p>
                </div>
            </section>

            {/* Founders Section */}
            <section className="border-t border-slate-200 bg-slate-50">
                <div className="max-w-6xl mx-auto px-6 py-24 md:py-32">
                    <div className="mb-16 text-center">
                        <p className="text-xs uppercase tracking-wider text-slate-400 mb-4">{t.founders.label}</p>
                        <h2 className="text-4xl md:text-5xl font-light mb-6" style={{ color: '#273B7D' }}>
                            {t.founders.title}
                        </h2>
                        <p className="text-xl text-slate-600 font-light max-w-3xl mx-auto">
                            {t.founders.description}
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-12 lg:gap-20">
                        {/* Founder */}
                        <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200">
                            <div className="w-20 h-20 rounded-full mb-6 flex items-center justify-center text-2xl font-bold text-white" style={{ backgroundColor: '#273B7D' }}>
                                HS
                            </div>
                            <h3 className="text-2xl font-bold text-slate-900 mb-2">{t.founders.founder.name}</h3>
                            <p className="text-lg font-medium mb-4" style={{ color: '#FF690F' }}>{t.founders.founder.role}</p>
                            <p className="text-slate-600 leading-relaxed mb-6 font-light">
                                {t.founders.founder.bio}
                            </p>
                            <div className="space-y-2">
                                <p className="text-sm font-semibold text-slate-900 mb-3">Expertise:</p>
                                <div className="flex flex-wrap gap-2">
                                    {t.founders.founder.expertise.map((skill, idx) => (
                                        <span key={idx} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm font-medium">
                                            {skill}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Co-founder */}
                        <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200">
                            <div className="w-20 h-20 rounded-full mb-6 flex items-center justify-center text-2xl font-bold text-white" style={{ backgroundColor: '#FF690F' }}>
                                MS
                            </div>
                            <h3 className="text-2xl font-bold text-slate-900 mb-2">{t.founders.cofounder.name}</h3>
                            <p className="text-lg font-medium mb-4" style={{ color: '#499FDD' }}>{t.founders.cofounder.role}</p>
                            <p className="text-slate-600 leading-relaxed mb-6 font-light">
                                {t.founders.cofounder.bio}
                            </p>
                            <div className="space-y-2">
                                <p className="text-sm font-semibold text-slate-900 mb-3">Expertise:</p>
                                <div className="flex flex-wrap gap-2">
                                    {t.founders.cofounder.expertise.map((skill, idx) => (
                                        <span key={idx} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm font-medium">
                                            {skill}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Mission & Values Section */}
            <section id="mission" className="border-t border-slate-200">
                <div className="max-w-6xl mx-auto px-6 py-24 md:py-32">
                    <div className="mb-20 text-center">
                        <p className="text-xs uppercase tracking-wider text-slate-400 mb-4">{t.mission.label}</p>
                        <h2 className="text-4xl md:text-5xl font-light mb-6" style={{ color: '#273B7D' }}>
                            {t.mission.title}
                        </h2>
                        <p className="text-xl text-slate-600 font-light max-w-3xl mx-auto">
                            {t.mission.description}
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
                        {t.mission.values.map((value, idx) => {
                            const IconComponent = valueIcons[value.icon as keyof typeof valueIcons];
                            return (
                                <div key={idx} className="group">
                                    <div className={`flex items-start gap-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                        <div className="w-16 h-16 rounded-xl border border-slate-200 flex items-center justify-center mb-4 group-hover:border-slate-900 transition-colors" style={{ backgroundColor: '#F8FAFC' }}>
                                            <IconComponent className="w-8 h-8 text-slate-400 group-hover:text-slate-900 transition-colors" />
                                        </div>
                                        <div className="flex-1 pt-2">
                                            <h3 className="text-2xl font-bold mb-3 text-slate-900">{value.title}</h3>
                                            <p className="text-slate-600 leading-relaxed font-light">{value.desc}</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Story Timeline Section */}
            <section id="story" className="border-t border-slate-200 bg-slate-50">
                <div className="max-w-6xl mx-auto px-6 py-24 md:py-32">
                    <div className="mb-20 text-center">
                        <p className="text-xs uppercase tracking-wider text-slate-400 mb-4">{t.story.label}</p>
                        <h2 className="text-4xl md:text-5xl font-light mb-6" style={{ color: '#273B7D' }}>
                            {t.story.title}
                        </h2>
                        <p className="text-xl text-slate-600 font-light max-w-3xl mx-auto">
                            {t.story.description}
                        </p>
                    </div>

                    <div className="space-y-12">
                        {t.story.timeline.map((item, idx) => (
                            <div key={idx} className={`flex items-start gap-8 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <div className="flex-shrink-0">
                                    <div className="w-20 h-20 rounded-full flex items-center justify-center text-xl font-bold text-white" style={{ backgroundColor: idx % 2 === 0 ? '#273B7D' : '#FF690F' }}>
                                        {item.year}
                                    </div>
                                </div>
                                <div className={`flex-1 bg-white rounded-xl p-8 shadow-sm border border-slate-200 ${idx % 2 === 1 ? 'md:ml-12' : ''}`}>
                                    <h3 className="text-2xl font-bold text-slate-900 mb-3">{item.title}</h3>
                                    <p className="text-slate-600 leading-relaxed font-light">{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="border-t border-slate-200">
                <div className="max-w-4xl mx-auto px-6 py-24 md:py-32 text-center">
                    <h2 className="text-4xl md:text-5xl font-light mb-6" style={{ color: '#273B7D' }}>
                        {t.cta.title}
                    </h2>
                    <p className="text-xl text-slate-600 mb-12 font-light max-w-2xl mx-auto">
                        {t.cta.description}
                    </p>
                    <div className={`flex flex-col sm:flex-row items-center justify-center gap-6 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                        <a href="https://demo.karia.tn" className="text-white px-10 py-4 rounded-full transition-all duration-300 text-base font-semibold shadow-[0_10px_20px_-5px_rgba(255,105,15,0.4)] hover:shadow-[0_15px_30px_-5px_rgba(255,105,15,0.5)] hover:-translate-y-0.5 active:translate-y-0" style={{ backgroundColor: '#FF690F' }}>
                            {t.cta.primary}
                        </a>
                        <a href="/" className={`flex items-center gap-3 transition-all duration-300 text-base font-medium hover:gap-4 ${isRTL ? 'flex-row-reverse' : ''}`} style={{ color: '#273B7D' }}>
                            <span>{t.cta.secondary}</span>
                            <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
                        </a>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-slate-200">
                <div className="max-w-6xl mx-auto px-6 py-16">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-8">
                        <div className={`flex flex-col items-center md:items-start ${isRTL ? 'md:items-end' : ''}`}>
                            <a href="/" className="text-4xl mb-4" style={{ fontFamily: 'Caveat, cursive', color: '#273B7D', fontWeight: 700 }}>Karia</a>
                            <p className="text-slate-600 text-center md:text-left max-w-md">
                                {t.footer.tagline}
                            </p>
                        </div>

                        <div className={`grid grid-cols-3 gap-12 text-center md:text-left ${isRTL ? 'md:text-right' : ''}`}>
                            <div>
                                <h3 className="font-semibold text-slate-900 mb-4">{t.footer.product}</h3>
                                <ul className="space-y-3 text-slate-600">
                                    <li><a href="/" className="hover:text-slate-900 transition-colors">{t.footer.features}</a></li>
                                    <li><a href="/" className="hover:text-slate-900 transition-colors">{t.footer.pricingLink}</a></li>
                                    <li><a href="/" className="hover:text-slate-900 transition-colors">{t.footer.updates}</a></li>
                                </ul>
                            </div>

                            <div>
                                <h3 className="font-semibold text-slate-900 mb-4">{t.footer.company}</h3>
                                <ul className="space-y-3 text-slate-600">
                                    <li><a href="/about" className="hover:text-slate-900 transition-colors">{t.footer.about}</a></li>
                                    <li><a href="/" className="hover:text-slate-900 transition-colors">{t.footer.blog}</a></li>
                                    <li><a href="/" className="hover:text-slate-900 transition-colors">{t.footer.contact}</a></li>
                                </ul>
                            </div>

                            <div>
                                <h3 className="font-semibold text-slate-900 mb-4">{t.footer.legal}</h3>
                                <ul className="space-y-3 text-slate-600">
                                    <li><a href="/" className="hover:text-slate-900 transition-colors">{t.footer.privacy}</a></li>
                                    <li><a href="/" className="hover:text-slate-900 transition-colors">{t.footer.terms}</a></li>
                                    <li><a href="/" className="hover:text-slate-900 transition-colors">{t.footer.security}</a></li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-slate-200 mt-12 pt-8 text-center text-slate-500">
                        <p>{t.footer.rights}</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default AboutPage;